import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { FiEdit3, FiZap } from 'react-icons/fi';
import { useHistory, useLocation } from 'react-router-dom';
import Container from '~/components/container';
import ContainerFlex from '~/components/containerFlex';

import { icons } from '~/assets';
import Button from '~/components/button';
import Dialog from '~/components/dialog';
import Loading from '~/components/loading';
import useApi, { ApiMethod } from '~/hooks/useApi';
import Endpoints from '~/services/endpoints';
import { currencyUnmasked } from '~/utils/masks';
import { isDateValid } from '~/utils/validators';
import * as St from '../style';
import AbaAutomatica, {
  getInitialAutomaticTabData,
  IAutomaticTabData,
} from './components/abaAutomatica';
import AbaManual from './components/abaManual';
import CampaingData, { IFormCampaingData } from './components/campaingData';
import { IResponseDetailsCampaing } from './models/interfaceModels';
import { getDetailsCampaing } from './services/campaingService';

export interface IResponseCreateCampaing {
  id: string;
  criterioPesquisa: {
    baseDados: string;
    segmentacoes: string[];
    filtrosPesquisa: {
      estados: string[];
      cidades: string[];
      bairros: string[];
      statusCartao: string[];
      rangeLimiteDisponivel: number[];
    };
  };
  dataValidade: string | null;
  dataAgendamento: string | null;
  canal: string | null;
  tipo: string;
  status: string;
  nome: string;
  totalAtingidos: number | null;
}

type ICampaignTab = 'AUTOMATICA' | 'MANUAL';

const Communication: React.FC = () => {
  const state = useLocation() as any;
  const history = useHistory();

  const [form, setForm] = useState<IFormCampaingData>({
    descCampanha: '',
    dtaValidade: moment(new Date()).format('DD/MM/YYYY'),
    dtaInicio: moment(new Date()).add(1, 'days').format('DD/MM/YYYY'),
    horaInicio: moment(new Date()).format('HH:mm'),
    horaValidade: moment(new Date()).add(1, 'hours').format('HH:mm'),
    switchDtaExecucao: false,
  });
  const [automaticTabData, setAutomaticTabData] = useState<IAutomaticTabData>(
    getInitialAutomaticTabData,
  );
  const [activeTab, setActiveTab] = useState<ICampaignTab>('AUTOMATICA');
  const [infoDialog, setInfoDialog] = useState({
    open: false,
    message: '',
    error: false,
    isGoBack: false,
  });
  const [infoDialogInsert, setInfoDialogInsert] = useState({
    open: false,
  });
  const [idCampaing, setIdCampaing] = useState<string>(state.state?.idCampaing);

  const disableTabsClick = !!state.state?.idCampaing;

  const PUTCreateCampaignV2 = useApi<IResponseCreateCampaing>(
    Endpoints.fidelizacao.putCreateCampaignV2,
    ApiMethod.PUT,
  );

  const POSTInsertCampaignManually = useApi<IResponseCreateCampaing>(
    Endpoints.fidelizacao.postInsertCampaignManually,
    ApiMethod.POST,
  );

  const GETDetailsCampaing = useApi<IResponseDetailsCampaing>(
    Endpoints.fidelizacao.getDetailsCampaign.replace(
      'PARAM_ID_CAMPANHA',
      idCampaing,
    ),
    ApiMethod.GET,
  );

  /**
   * Função que atualiza a campanha com os dados preenchidos em tela.
   */
  async function handleCreateCampaign() {
    const payload = {
      nome: form.descCampanha,
      // dataValidade: transformInGlobalDate(form.dtaValidade),
      dataValidade: !form.switchDtaExecucao
        ? `${form.dtaValidade.split('/')[2]}-${
            form.dtaValidade.split('/')[1]
          }-${form.dtaValidade.split('/')[0]}T${form.horaValidade}:00`
        : null,
      dataAgendamento: form.switchDtaExecucao
        ? `${form.dtaInicio.split('/')[2]}-${form.dtaInicio.split('/')[1]}-${
            form.dtaInicio.split('/')[0]
          }T${form.horaInicio}:00`
        : null,
      tipo: 'COMUNICACAO',
      criterioPesquisa: {
        baseDados: automaticTabData.baseDados,
        segmentacoes: automaticTabData.selected.map((item) => item.value),
        filtrosPesquisa: {
          estados: automaticTabData.formsCriteriosFiltros.ufSelects.map(
            (item) => item.value,
          ),
          cidades: automaticTabData.formsCriteriosFiltros.cidadesSelects.map(
            (item) => item.value,
          ),
          bairros: automaticTabData.formsCriteriosFiltros.bairrosSelects.map(
            (item) => item.value,
          ),
          statusCartao: automaticTabData.formsCriteriosFiltros.statusSelects.map(
            (item) => item.value,
          ),
          rangeLimiteDisponivel: [
            currencyUnmasked(
              automaticTabData.formsCriteriosFiltros.limitMinDisponivel,
            ),
            currencyUnmasked(
              automaticTabData.formsCriteriosFiltros.limitMaxDisponivel,
            ),
          ],
        },
      },
    };

    const response = await PUTCreateCampaignV2.callApi(payload);

    if (response.status >= 200 && response.status < 300) {
      setInfoDialog({
        open: true,
        message: 'Campanha criada com sucesso!',
        error: false,
        isGoBack: true,
      });
    } else if (response.status === 400) {
      setInfoDialog({
        open: true,
        message:
          response.status + ' - ' + response.mensagem ||
          'Ocorreu um erro ao criar a campanha. Tente novamente mais tarde.',
        error: true,
        isGoBack: false,
      });
    } else {
      setInfoDialog({
        open: true,
        message:
          response.status + ' - ' + response.mensagem ||
          'Ocorreu um erro ao criar a campanha. Tente novamente mais tarde.',
        error: true,
        isGoBack: true,
      });
    }
  }

  /**
   * Verifica os campos obrigatórios para habilitar/desabilitar o botão de criar campanha
   *
   * @returns Se desabilita o Btn.
   */
  const handleDisableCreateButton = (): boolean => {
    const {
      ufSelects,
      cidadesSelects,
      bairrosSelects,
      statusSelects,
      limitMinDisponivel,
      limitMaxDisponivel,
    } = automaticTabData.formsCriteriosFiltros;
    let disable = false;
    const hasAddressFilterSelected =
      ufSelects.length > 0 ||
      cidadesSelects.length > 0 ||
      bairrosSelects.length > 0;

    if (form.descCampanha.trim() === '') {
      disable = true;
    }
    // Verifica Data de validade ou Data de execução
    if (form.switchDtaExecucao) {
      if (!isDateValid(form.dtaInicio) || form.horaInicio.trim() === '') {
        disable = true;
      }
    } else {
      if (!isDateValid(form.dtaValidade) || form.horaValidade.trim() === '') {
        disable = true;
      }
    }
    // Verifica se há segmentações selecionadas
    if (automaticTabData.selected.length === 0) {
      disable = true;
    }
    // Verifica se há filtros preenchidos
    if (
      automaticTabData.selected.filter((item) => item.value === 'BAIRRO')
        .length > 0
    ) {
      if (!hasAddressFilterSelected) {
        disable = true;
      }
    }
    // Verifica se é limite disponivel
    if (
      automaticTabData.selected.filter(
        (item) => item.value === 'LIMITE_DISPONIVEL',
      ).length > 0
    ) {
      if (
        limitMinDisponivel.trim() === '' ||
        limitMaxDisponivel.trim() === ''
      ) {
        disable = true;
      }
    }
    // Verifica se é status do cartão
    if (
      automaticTabData.selected.filter((item) => item.value === 'STATUS_CARTAO')
        .length > 0
    ) {
      if (statusSelects.length === 0) {
        disable = true;
      }
    }
    return disable;
  };

  const handleDisableCreateManualButton = (): boolean => {
    if (form.descCampanha.trim() === '') {
      return true;
    }

    if (form.switchDtaExecucao) {
      return !isDateValid(form.dtaInicio) || form.horaInicio.trim() === '';
    }

    if (
      POSTInsertCampaignManually.status >= 200 &&
      POSTInsertCampaignManually.status < 300
    ) {
      return true;
    }

    return !isDateValid(form.dtaValidade) || form.horaValidade.trim() === '';
  };

  /**
   * Função que cria a campanha manual com os dados preenchidos em tela.
   */
  async function handleCreateCampaignManual() {
    const payload = {
      nome: form.descCampanha,
      dataValidade: !form.switchDtaExecucao
        ? `${form.dtaValidade.split('/')[2]}-${
            form.dtaValidade.split('/')[1]
          }-${form.dtaValidade.split('/')[0]}T${form.horaValidade}:00`
        : null,
      dataAgendamento: form.switchDtaExecucao
        ? `${form.dtaInicio.split('/')[2]}-${form.dtaInicio.split('/')[1]}-${
            form.dtaInicio.split('/')[0]
          }T${form.horaInicio}:00`
        : null,
    };

    const response = await POSTInsertCampaignManually.callApi(payload);

    if (response.status >= 200 && response.status < 300) {
      setIdCampaing(response.data.id);
      return response.data.id;
    } else if (response.status === 400) {
      setInfoDialog({
        open: true,
        message:
          response.status + ' - ' + response.mensagem ||
          'Ocorreu um erro ao criar a campanha. Tente novamente mais tarde.',
        error: true,
        isGoBack: false,
      });
      return;
    } else {
      setInfoDialog({
        open: true,
        message:
          response.status + ' - ' + response.mensagem ||
          'Ocorreu um erro ao criar a campanha. Tente novamente mais tarde.',
        error: true,
        isGoBack: true,
      });
      return;
    }
  }

  useEffect(() => {
    if (!idCampaing) return;

    setActiveTab('MANUAL');
    getDetailsCampaing(GETDetailsCampaing, setForm, setInfoDialog);
  }, [idCampaing]);

  return (
    <>
      <Loading
        status={
          PUTCreateCampaignV2.loading || POSTInsertCampaignManually.loading
        }
      />
      <Dialog
        icon={infoDialog.error ? icons.exclamation : icons.check}
        open={infoDialog?.open}
        title="Detalhes"
        widthInPercent={40}
        positiveLabel="Entendi"
        positiveAction={() => {
          setInfoDialog({
            message: '',
            open: false,
            error: false,
            isGoBack: false,
          });
          if (infoDialog.isGoBack) {
            if (!infoDialog.error) {
              history.push('/communication-preview', {
                idCampaing: PUTCreateCampaignV2.data.id,
                nameCampaing: PUTCreateCampaignV2.data.nome,
                statusCampaing: PUTCreateCampaignV2.data.status,
              });
            } else {
              history.push('/campanha');
            }
          }
        }}
        message={infoDialog?.message}
      />
      <Dialog
        icon={icons.exclamation}
        open={infoDialogInsert?.open}
        title="Campanha manual"
        widthInPercent={40}
        positiveLabel="Criar campanha"
        positiveAction={() => {
          handleCreateCampaignManual();
          setInfoDialogInsert({
            open: false,
          });
        }}
        negativeLabel="Não"
        negativeAction={() => {
          setInfoDialogInsert({
            open: false,
          });
          history.push('/campanha');
        }}
        message="Deseja criar uma campanha manual e inserir os clientes selecionados?"
      />

      <Container>
        <St.BoxCard>
          <ContainerFlex padding={8} row space gap="32">
            <St.Title>Tipo de campanha: {state.state?.tipo}</St.Title>
          </ContainerFlex>

          <ContainerFlex padding={8} wrap>
            <CampaingData
              form={form}
              setForm={setForm}
              isManualTab={activeTab === 'MANUAL'}
              onClickBtnCreateCampaign={() =>
                setInfoDialogInsert({
                  open: true,
                })
              }
              disabledBtnCreateCampaign={handleDisableCreateManualButton()}
              isPreview={!!state.state?.idCampaing}
            />

            <St.TabGroup>
              <St.TabButton
                type="button"
                $active={activeTab === 'AUTOMATICA'}
                $disabled={disableTabsClick}
                onClick={() => {
                  if (!disableTabsClick) {
                    setActiveTab('AUTOMATICA');
                  }
                }}
                disabled={disableTabsClick}
              >
                <FiZap size={18} />
                <span>Campanha Automatica</span>
              </St.TabButton>

              <St.TabButton
                type="button"
                $active={activeTab === 'MANUAL'}
                $disabled={disableTabsClick}
                onClick={() => {
                  if (!disableTabsClick) {
                    setActiveTab('MANUAL');
                  }
                }}
                disabled={disableTabsClick}
              >
                <FiEdit3 size={18} />
                <span>Campanha Manual</span>
              </St.TabButton>
            </St.TabGroup>

            {activeTab === 'AUTOMATICA' && (
              <AbaAutomatica onChange={setAutomaticTabData} />
            )}

            {activeTab === 'MANUAL' && (
              <AbaManual
                form={form}
                idCampaing={idCampaing}
                isPreview={!!idCampaing}
                detailsCampaing={GETDetailsCampaing.data}
                onCancel={() =>
                  getDetailsCampaing(GETDetailsCampaing, setForm, setInfoDialog)
                }
              />
            )}

            <ContainerFlex row wrap end marginTop={30} marginBottom={20}>
              <Button
                borderRadius={10}
                fitContent
                outlined
                onClick={() => history.goBack()}
              >
                <span>Voltar</span>
              </Button>
              {activeTab === 'AUTOMATICA' && (
                <Button
                  borderRadius={10}
                  marginLeft={10}
                  fitContent
                  onClick={() => handleCreateCampaign()}
                  disabled={handleDisableCreateButton()}
                >
                  <span>Gerar</span>
                </Button>
              )}
            </ContainerFlex>
          </ContainerFlex>
        </St.BoxCard>
      </Container>
    </>
  );
};

export default Communication;
